import axios, { AxiosInstance, AxiosRequestConfig } from 'axios';
import Endpoints from './models/endpoints';
import validateFile from './lib/validators/validate-file';

class LowerApi {
  private readonly baseEndpoint: string;

  private authCode?: string;

  private readonly axiosInstance: AxiosInstance;

  constructor({
    authCode, baseEndpoint, timeout = 60000,
  }: {
    baseEndpoint: string;
    authCode?: string;
    timeout?: number;
  }) {
    this.baseEndpoint = baseEndpoint;
    this.authCode = authCode;
    this.axiosInstance = axios.create({
      baseURL: baseEndpoint,
      timeout,
    });
  }

  private buildRequest({ endpoint, options = {} }: {
    endpoint: Endpoints;
    options?: AxiosRequestConfig;
  }) {
    if (!this.authCode) {
      throw new Error('LowerApi authentication is not valid.');
    }

    const headers = {
      'X-Upload-Code': this.authCode,
    };

    const config = {
      headers,
      ...options,
    };

    return {
      config,
      url: `${this.baseEndpoint}${endpoint}`,
    };
  }

  private async request({ endpoint, options = {} }: {
    endpoint: Endpoints;
    options?: AxiosRequestConfig;
  }) {
    const { url, config } = this.buildRequest({
      endpoint,
      options,
    });

    return this.axiosInstance(url, config);
  }

  public async authenticate({ authCode }: {
    authCode: string;
  }) {
    this.authCode = authCode;

    return this.request({
      endpoint: Endpoints.UserVerify,
    });
  }

  /**
   * Uploads a file to the lower api.
   *
   * @param file - File object.
   * @param options - Axios request configuration object.
   * @param {Function} validate - Function that takes a File object and returns true or false.
   */
  public async uploadFile(
    file: File,
    options: AxiosRequestConfig = {},
    validate = () => validateFile(file).isValid(),
  ) {
    if (!validate()) {
      throw new Error('File did not pass validation.');
    }

    const data = new FormData();

    data.append('file', file);

    return this.request({
      endpoint: Endpoints.FileUpload,
      options: {
        data,
        method: 'POST',
        ...options,
      },
    });
  }
}

export default LowerApi;
